import { SlicePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, submit } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { OshAnalysisDictionaryDropdownComponent } from '@app/components/osh/osh-analysis-dictionary-dropdown/osh-analysis-dictionary-dropdown.component';
import { AvatarComponent } from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { LabelComponent } from '@app/shared/components/label/label.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { DictionaryDto, LookupType } from '@app/shared/services/dtos/dictionary.dto';
import { LookupService } from '@app/shared/services/lookup.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { Company } from '@app/shared/types/models/company/company.model';
import { AddOshAnalysisDialogData, CreateOrEditOshAnalysisDialogData } from '@app/shared/types/models/osh/osh.model';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { User } from '@app/shared/types/models/user/user.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDatePickerComponent } from '@app/shared/ui/date-picker/ui-date-picker.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe } from '@ngx-translate/core';
import {
  calculateRiskLevel,
  ConsequencesEnum,
  createDangerousSituation,
  createOshAnalysisModel,
  OshAnalysisFormModel,
  oshAnalysisSchema,
  OshRiskLevel,
  ProbabilityEnum,
  toDangerousSituationRequest,
  toDictionaryDto
} from './add-osh-analysis.builder';

interface DangerousSituationRiskLevels {
  primary: OshRiskLevel;
  secondary: OshRiskLevel;
}

@Component({
  selector: 'proffeo-add-osh-analysis-dialog',
  imports: [
    UiSelectComponent,
    FormField,
    TranslatePipe,
    MatDialogModule,
    UiDialogComponent,
    MatButtonModule,
    UiInputComponent,
    LabelComponent,
    UiDatePickerComponent,
    UiTextareaComponent,
    MatSelectModule,
    MatCheckboxModule,
    MatError,
    AvatarComponent,
    OshAnalysisDictionaryDropdownComponent,
    SlicePipe,
    UiButtonComponent
  ],
  templateUrl: './add-osh-analysis-dialog.component.html',
  styleUrl: './add-osh-analysis-dialog.component.scss'
})
export class AddOshAnalysisDialogComponent {
  private readonly dialogRef: MatDialogRef<AddOshAnalysisDialogComponent> = inject(
    MatDialogRef<AddOshAnalysisDialogComponent>
  );
  private readonly lookupService: LookupService = inject(LookupService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly dialogData: AddOshAnalysisDialogData = inject(MAT_DIALOG_DATA);
  protected readonly LookupTpe: typeof LookupType = LookupType;
  protected readonly AvatarSize: typeof AvatarSize = AvatarSize;

  protected readonly getWorkTypeDictionary = this.lookupService.getDictionary(LookupType.WorkType);
  protected readonly getWorkPossibleThreatDictionary = this.lookupService.getDictionary(LookupType.WorkPossibleThreat);
  protected readonly getPossibleThreatDictionary = this.lookupService.getDictionary(LookupType.PossibleThreat);
  protected readonly getRemediesDictionary = this.lookupService.getDictionary(LookupType.Remedy);
  protected readonly activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  protected readonly isNewRemediesDictionaryItem = signal(false);
  protected readonly newRemediesDictionaryItem = signal('');

  protected readonly model = signal<OshAnalysisFormModel>(createOshAnalysisModel(this.dialogData.oshAnalysis));
  protected readonly form = form(this.model, oshAnalysisSchema);

  /** Not a form field: `User` instances (avatars need the class) are kept next to the plain form model. */
  protected readonly assignedUsers = signal<User[]>(this.dialogData.oshAnalysis?.assignedUsers() ?? []);

  /** Risk level per dangerous situation, derived once per model change (not per change detection). */
  protected readonly riskLevels = computed((): DangerousSituationRiskLevels[] =>
    this.model().dangerousSituations.map(situation => ({
      primary: calculateRiskLevel(situation.probability, situation.consequences),
      secondary: calculateRiskLevel(
        situation.secondaryRiskAnalysis.probability,
        situation.secondaryRiskAnalysis.consequences
      )
    }))
  );

  public readonly consequences: DropdownItem<ConsequencesEnum>[] = [
    { value: ConsequencesEnum.VeryLow, label: 'osh.consequences-very-low' },
    { value: ConsequencesEnum.Low, label: 'osh.consequences-low' },
    { value: ConsequencesEnum.Medium, label: 'osh.consequences-medium' },
    { value: ConsequencesEnum.High, label: 'osh.consequences-high' },
    { value: ConsequencesEnum.VeryHigh, label: 'osh.consequences-very-high' }
  ];
  public readonly probabilities: DropdownItem<ProbabilityEnum>[] = [
    { value: ProbabilityEnum.Rare, label: 'osh.probability-rare' },
    { value: ProbabilityEnum.Unlikely, label: 'osh.probability-unlikely' },
    { value: ProbabilityEnum.Likely, label: 'osh.probability-possible' },
    { value: ProbabilityEnum.Possible, label: 'osh.probability-probable' },
    { value: ProbabilityEnum.AlmostCertain, label: 'osh.probability-almost-certain' }
  ];

  protected readonly compareFn = (o1: DictionaryDto, o2: DictionaryDto): boolean => !!o1 && !!o2 && o1.id === o2.id;

  protected onNewRemediesCheckboxChange(event: MatCheckboxChange): void {
    this.isNewRemediesDictionaryItem.set(event.checked);
  }

  protected cancelNewRemediesDictionaryItem(): void {
    this.newRemediesDictionaryItem.set('');
    this.isNewRemediesDictionaryItem.set(false);
  }

  protected onRemediesChange(index: number, remedies: DictionaryDto[]): void {
    this.updateDangerousSituation(index, { remedies: remedies.map(remedy => toDictionaryDto(remedy)) });
    this.form.dangerousSituations[index].remedies().markAsTouched();
  }

  protected onRemediesOpenedChange(index: number, opened: boolean): void {
    if (!opened) {
      this.form.dangerousSituations[index].remedies().markAsTouched();
    }
  }

  protected handleSaveClick(): void {
    void submit(this.form, {
      action: async () => {
        this.dialogRef.close(this.toRequest());
      }
    });
  }

  protected onNewRemediesDictionaryItemChange(value: ProffeoUiInputValue): void {
    this.newRemediesDictionaryItem.set(String(value ?? ''));
  }

  protected addOption(): void {
    const trimmed = this.newRemediesDictionaryItem().trim();
    if (trimmed && !this.getRemediesDictionary().find(v => v.value === trimmed)) {
      this.lookupService
        .addItemToDictionary(LookupType.Remedy, {
          companyId: this.activeCompany()?.id,
          type: LookupType.Remedy,
          value: trimmed,
          id: null
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ error: () => undefined });
    }
  }

  protected addNextSituation(): void {
    this.model.update(model => ({
      ...model,
      dangerousSituations: [...model.dangerousSituations, createDangerousSituation()]
    }));
  }

  public assignUsers(): void {
    this.dialogService
      .selectUsersToOshAnalysis(this.assignedUsers(), null)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) {
          return;
        }
        const removedIds = new Set(result.usersToRemove.map(user => user.id));
        this.assignedUsers.update(users => [...users.filter(user => !removedIds.has(user?.id)), ...result.usersToAdd]);
      });
  }

  private updateDangerousSituation(
    index: number,
    changes: Partial<OshAnalysisFormModel['dangerousSituations'][number]>
  ): void {
    this.model.update(model => ({
      ...model,
      dangerousSituations: model.dangerousSituations.map((situation, i) =>
        i === index ? { ...situation, ...changes } : situation
      )
    }));
  }

  private toRequest(): CreateOrEditOshAnalysisDialogData {
    const model = this.model();
    const request: CreateOrEditOshAnalysisDialogData = {
      assignedUserIds: this.assignedUsers().map(user => user?.id),
      workDescription: model.workDescription,
      workPossibleThreatId: model.workPossibleThreat?.id,
      workStartDate: this.toWorkStartDateRequest(model.workStartDate),
      workTypeId: model.workType?.id,
      path: model.workType?.id,
      parentId: null,
      dangerousSituations: model.dangerousSituations.map(situation => toDangerousSituationRequest(situation))
    };
    return this.dialogData.oshAnalysis ? { ...request, id: this.dialogData.oshAnalysis.id } : request;
  }

  /** Unchanged date → the original API string (as before); a picked date → its JSON (ISO) form. */
  private toWorkStartDateRequest(date: Date | null): string {
    const original = this.dialogData.oshAnalysis?.workStartDate;
    if (!date) {
      return null;
    }
    return original && new Date(original).getTime() === date.getTime() ? original : date.toISOString();
  }
}
